(() => {
  const init = (section) => {
    if (!section || section.dataset.phUgcMarqueeReady === 'true') return;

    const track = section.querySelector('[data-ph-ugc-track]');
    if (!track) return;

    section.dataset.phUgcMarqueeReady = 'true';

    const autoplay = section.dataset.phUgcAutoplay === 'true';
    const speed = Math.max(Number(section.dataset.phUgcSpeed) || 12, 1);
    const originals = Array.from(track.children).filter((card) => !card.hasAttribute('data-ph-ugc-clone'));
    let loopPoint = 0;
    let pixelCarry = 0;
    let manualPauseUntil = 0;
    let animationFrame = 0;
    let lastFrameTime = performance.now();
    let isNearViewport = false;
    let visibilityObserver;
    let pointerId = null;
    let pointerStartX = 0;
    let pointerLastX = 0;
    let dragging = false;
    let dragged = false;

    const cleanClone = (clone) => {
      clone.dataset.phUgcClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      [clone, ...clone.querySelectorAll('*')].forEach((node) => {
        Array.from(node.attributes || []).forEach((attribute) => {
          if (attribute.name === 'id' || attribute.name.startsWith('data-shopify')) {
            node.removeAttribute(attribute.name);
          }
        });
      });
      return clone;
    };

    const appendCloneSet = () => {
      originals.forEach((card) => track.appendChild(cleanClone(card.cloneNode(true))));
    };

    const buildLoop = () => {
      track.querySelectorAll('[data-ph-ugc-clone]').forEach((clone) => clone.remove());
      loopPoint = 0;
      if (!autoplay || originals.length === 0) return;

      appendCloneSet();
      const firstClone = track.querySelector('[data-ph-ugc-clone]');
      if (firstClone) loopPoint = firstClone.offsetLeft - originals[0].offsetLeft;

      let sets = 1;
      while (loopPoint > 0 && track.scrollWidth < track.clientWidth + loopPoint && sets < 8) {
        appendCloneSet();
        sets += 1;
      }

      bindVideoPlayback(track);
    };

    const videoIsPlaying = () => Array.from(track.querySelectorAll('video')).some((video) => !video.paused && !video.ended);

    const moveOneFrame = (time) => {
      if (!autoplay || loopPoint <= 0 || document.hidden || performance.now() < manualPauseUntil || videoIsPlaying()) return;

      const elapsed = Math.min(Math.max(time - lastFrameTime, 0), 100);
      pixelCarry += speed * elapsed / 1000;
      const pixels = Math.floor(pixelCarry);
      if (pixels < 1) return;

      pixelCarry -= pixels;
      track.scrollLeft += pixels;
      if (track.scrollLeft >= loopPoint) track.scrollLeft -= loopPoint;
    };

    const animate = (time) => {
      if (!isNearViewport) {
        animationFrame = 0;
        return;
      }

      moveOneFrame(time);
      lastFrameTime = time;
      animationFrame = window.requestAnimationFrame(animate);
    };

    const startAnimation = () => {
      if (!autoplay || animationFrame) return;
      lastFrameTime = performance.now();
      animationFrame = window.requestAnimationFrame(animate);
    };

    const pauseForManualMove = () => {
      manualPauseUntil = performance.now() + 1600;
    };

    const bindVideoPlayback = (root) => {
      root.querySelectorAll('video').forEach((video) => {
        if (video.dataset.phUgcPlaybackReady === 'true') return;
        video.dataset.phUgcPlaybackReady = 'true';

        const pauseOtherVideos = () => {
          root.querySelectorAll('video').forEach((otherVideo) => {
            if (otherVideo !== video && !otherVideo.paused) otherVideo.pause();
          });
        };

        video.addEventListener('pointerdown', () => {
          pauseOtherVideos();
          manualPauseUntil = performance.now() + 3000;
        });

        video.addEventListener('play', () => {
          pauseOtherVideos();
          manualPauseUntil = Number.POSITIVE_INFINITY;
        });

        const resumeAfterPlayback = () => {
          manualPauseUntil = performance.now() + 1600;
        };

        video.addEventListener('pause', resumeAfterPlayback);
        video.addEventListener('ended', resumeAfterPlayback);
      });
    };

    const normalizeLoopPosition = () => {
      if (loopPoint <= 0) return;
      while (track.scrollLeft >= loopPoint) track.scrollLeft -= loopPoint;
      if (track.scrollLeft <= 0) track.scrollLeft += loopPoint;
    };

    track.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      pointerId = event.pointerId;
      pointerStartX = event.clientX;
      pointerLastX = event.clientX;
      dragging = false;
      dragged = false;
      pauseForManualMove();
    });

    track.addEventListener('pointermove', (event) => {
      if (pointerId === null || event.pointerId !== pointerId) return;

      const totalDistance = event.clientX - pointerStartX;
      if (!dragging && Math.abs(totalDistance) >= 4) {
        dragging = true;
        dragged = true;
        track.classList.add('is-dragging');
        track.setPointerCapture(pointerId);
      }

      if (!dragging) return;
      const distance = event.clientX - pointerLastX;
      pointerLastX = event.clientX;

      if (loopPoint > 0 && distance > 0 && track.scrollLeft <= distance + 1) {
        track.scrollLeft += loopPoint;
      }
      track.scrollLeft -= distance;
      normalizeLoopPosition();
      pauseForManualMove();
      event.preventDefault();
    });

    const finishDrag = (event) => {
      if (pointerId === null || (event.pointerId != null && event.pointerId !== pointerId)) return;
      if (track.hasPointerCapture(pointerId)) track.releasePointerCapture(pointerId);
      pointerId = null;
      dragging = false;
      track.classList.remove('is-dragging');
      pauseForManualMove();
      window.setTimeout(() => { dragged = false; }, 120);
    };

    track.addEventListener('pointerup', finishDrag);
    track.addEventListener('pointercancel', finishDrag);
    track.addEventListener('dragstart', (event) => event.preventDefault());
    track.addEventListener('click', (event) => {
      if (!dragged) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    let resizeTimer;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        buildLoop();
      }, 150);
    });

    bindVideoPlayback(track);
    buildLoop();

    if (autoplay && 'IntersectionObserver' in window) {
      visibilityObserver = new IntersectionObserver((entries) => {
        isNearViewport = entries[0].isIntersecting;
        if (isNearViewport) startAnimation();
      }, { rootMargin: '300px 0px' });
      visibilityObserver.observe(section);
    } else if (autoplay) {
      isNearViewport = true;
      startAnimation();
    }

    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.target.contains(section)) return;
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      visibilityObserver?.disconnect();
    });
  };

  const initAll = (root = document) => {
    root.querySelectorAll('.ph-ugc-video-grid').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
